import {
  AppDocsDocument,
  SearchUsersDocument,
  useSearchActivitiesQuery,
  useSearchQuotesQuery,
  useDeactivateUserMutation,
  useSubscriptionQuery,
  type User
} from '@paintscout/api'
import type { DialogProps } from '@paintscout/ui'
import { Link as MUILink } from '@material-ui/core'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Highlight,
  Typography,
  useDialogs,
  IconButton,
  useClientOptions,
  useUser,
  Skeleton,
  Alert
} from '@paintscout/ui'
import React, { useMemo, useRef } from 'react'
import ClearIcon from '@material-ui/icons/Clear'
import { addAuth0Prefix, getHighestRole, removeAuth0Prefix } from '@paintscout/util'
import * as Sentry from '@sentry/react'
import { getOperationName } from 'apollo-link'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { jsonToLucene } from 'json-to-lucene'
import { ManageEstimatesDialog } from './ManageEstimatesDialog'

export interface DeactivateUserDialogProps extends DialogProps {
  user: User
  onConfirm: () => void
}

export function DeactivateUserDialog({ user, onConfirm, ...props }: DeactivateUserDialogProps) {
  const { dismissDialog, openDialog } = useDialogs()
  const { clientId } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const userType = getHighestRole(user.app_metadata.roles)
  const [deactivateUser, { loading }] = useDeactivateUserMutation()
  const { billingId, isTrial, nonBilling, isSubscribed } = useUser()

  const userIdWithoutPrefix = removeAuth0Prefix(user.user_id)
  const userIdWithPrefix = addAuth0Prefix(userIdWithoutPrefix)
  const isAdminOrSales = ['admin', 'sales'].includes(userType)
  const nowRef = useRef(Date.now())

  const blockingEventsQuery = useMemo(() => {
    return jsonToLucene({
      filters: [
        {
          id: 'details.activityType',
          type: 'equal' as const,
          value: 'event'
        },
        {
          id: 'details.canceled',
          type: 'equal' as const,
          value: true,
          not: true
        },
        {
          type: 'or' as const,
          value: [
            {
              id: 'details.start',
              type: 'greaterThan' as const,
              value: nowRef.current
            },
            {
              id: 'details.end',
              type: 'greaterThan' as const,
              value: nowRef.current
            }
          ]
        },
        {
          id: 'details.owner.id',
          type: 'in' as const,
          value: [userIdWithoutPrefix, userIdWithPrefix]
        }
      ]
    })
  }, [userIdWithPrefix, userIdWithoutPrefix])

  const quotesQuery = useMemo(() => {
    return jsonToLucene({
      filters: [
        {
          id: 'trashed',
          type: 'equal' as const,
          value: true,
          not: true
        },
        {
          id: 'owner.id',
          type: 'in' as const,
          value: [userIdWithoutPrefix, userIdWithPrefix]
        }
      ]
    })
  }, [userIdWithPrefix, userIdWithoutPrefix])

  const { data: blockingEventsData, loading: loadingBlockingEvents } = useSearchActivitiesQuery({
    fetchPolicy: 'network-only',
    variables: {
      query: blockingEventsQuery,
      limit: 1
    }
  })
  const hasBlockingEvents = (blockingEventsData?.searchActivities?.total_rows || 0) > 0
  const { data: quotesData, loading: loadingQuotes } = useSearchQuotesQuery({
    fetchPolicy: 'network-only',
    skip: !isAdminOrSales,
    variables: {
      query: quotesQuery,
      limit: 1
    }
  })
  const hasExistingQuotes = (quotesData?.searchQuotes?.total_rows || 0) > 0

  const showPricingInfo = !nonBilling && !isTrial && isSubscribed

  const { data: subscriptionData, loading: subscriptionLoading } = useSubscriptionQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      clientBillingId: billingId
    }
  })

  const planEndDate = subscriptionData?.subscription?.current_term_end
    ? moment(subscriptionData.subscription.current_term_end * 1000).format('MMM D, YYYY')
    : ''

  const handleConfirm = () => {
    if (typeof BroadcastChannel != 'undefined') {
      const channel = new BroadcastChannel('subscription-updates')
      channel.postMessage('user-deactivated')
    }
    onConfirm()
  }

  const handleNext = async () => {
    if (isAdminOrSales && hasExistingQuotes) {
      openDialog(ManageEstimatesDialog, {
        user,
        onConfirm: handleConfirm
      })
    } else {
      try {
        await deactivateUser({
          variables: {
            id: user.user_id,
            companyId: clientId,
            transferType: null,
            transferQuotesTo: null
          },
          refetchQueries: [getOperationName(SearchUsersDocument), getOperationName(AppDocsDocument)]
        })
        enqueueSnackbar('User deactivated', { variant: 'success' })
        handleConfirm()
      } catch (error) {
        console.log(error)
        enqueueSnackbar('Unable to deactivate user', { variant: 'error' })
        Sentry.captureException(error, { tags: { userId: user.user_id } })
        dismissDialog()
      }
    }
  }

  return (
    <Dialog {...props}>
      <DialogTitle
        rightContent={
          <IconButton style={{ padding: 0, marginBottom: 2 }} onClick={dismissDialog}>
            <ClearIcon />
          </IconButton>
        }
      >
        <Typography variant="h2">{hasBlockingEvents ? 'Unable to Deactivate User' : 'Deactivate User'}</Typography>
      </DialogTitle>
      <DialogContent style={hasBlockingEvents ? { paddingBottom: 16 } : undefined}>
        {loadingBlockingEvents ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="85%" />
            </Grid>
          </Grid>
        ) : hasBlockingEvents ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography>
                  Before deactivating this user, you&apos;ll need to reassign or cancel their upcoming events.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography>
                This will deactivate the account for{' '}
                <Highlight>
                  {user.user_metadata.firstName} {user.user_metadata.lastName}
                </Highlight>
                {showPricingInfo ? '.' : ' and they will no longer be able to log in.'}
              </Typography>
            </Grid>
            {showPricingInfo && (
              <Grid item xs={12}>
                <Typography>
                  {subscriptionLoading ? (
                    <>
                      <Skeleton variant="text" width="100%" />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="90%" />
                    </>
                  ) : (
                    <>
                      They will no longer be able to log in, and their seat will become available. If the seat isn’t
                      reassigned before your next billing date on <strong>{planEndDate}</strong>, it will be
                      automatically removed from your plan at that time.
                    </>
                  )}
                </Typography>
              </Grid>
            )}

            {showPricingInfo && (
              <Grid item xs={12}>
                <Typography>
                  All changes are subject to{' '}
                  <MUILink
                    href="https://www.paintscout.com/terms-of-service/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    subscriber terms
                  </MUILink>
                  .
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      {!hasBlockingEvents && (
        <DialogActions>
          <Button
            onClick={handleNext}
            loading={loading || subscriptionLoading || loadingQuotes || loadingBlockingEvents}
          >
            {isAdminOrSales && hasExistingQuotes ? 'Next' : 'Confirm'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
